import { inject, Injectable } from '@angular/core';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { tapResponse } from '@ngrx/operators';
import { EMPTY, exhaustMap, pipe } from 'rxjs';
import { LoggingService } from '../../../shared/services/core/logging.service';
import { BadgeStatus } from '../badges/badge-status.enum';
import { ScorecardV2Service } from './scorecard-v2.service';
import { ScorecardV2Store } from './scorecard-v2.store';

/**
 * Orchestrates all side effectful actions for the scorecard (v2) domain.
 *
 * Responsibilities:
 * - Coordinates API calls via ScorecardV2Service
 * - Updates the ScorecardV2Store in response to API results
 * - Applies local updates (for example from SignalR) to scorecards
 *
 * The badges from the `scorecard/v2` response are kept in the ScorecardV2Store as well.
 * The separate `scorecard/v2/badges` endpoint is still handled by BadgesService and BadgesV2Store.
 */
@Injectable({ providedIn: 'root' })
export class ScorecardFacade {
  private readonly scorecardService = inject(ScorecardV2Service);
  private readonly scorecardStore = inject(ScorecardV2Store);
  private readonly logService = inject(LoggingService);

  /**
   * Fetches all scorecards (v2) and their badges and populates the store. Skips the API call if the cache is still valid (TTL 60s).
   * Calls made while a fetch is in flight are ignored, so several components can call this on init without cancelling each other.
   */
  readonly fetchScorecards = rxMethod<void>(
    pipe(
      exhaustMap(() => {
        if (this.scorecardStore.isCacheValid()) {
          return EMPTY;
        }
        this.scorecardStore.setLoading(true);
        return this.scorecardService.fetchScorecards().pipe(
          tapResponse({
            next: (response) => {
              this.scorecardStore.set(response?.Scorecards ?? []);
              this.scorecardStore.setBadges(response?.Badges ?? []);
            },
            error: (error) => this.logService.error('Failed to fetch scorecards', error),
            finalize: () => this.scorecardStore.setLoading(false),
          })
        );
      })
    )
  );

  /**
   * Marks a scorecard action as completed and increments the scorecard's completed count.
   * An action that is already completed is ignored, so a repeated message does not count twice.
   */
  setActionCompleted(scorecardId: number, scorecardActionId: number): void {
    this.scorecardStore.updateById(scorecardId, (scorecard) => {
      const action = scorecard.Actions.find((a) => a.ScorecardActionId === scorecardActionId);
      if (!action || action.ActionCompleted) {
        return {};
      }
      return {
        ActionsCompleted: scorecard.ActionsCompleted + 1,
        Actions: scorecard.Actions.map((a) =>
          a.ScorecardActionId === scorecardActionId
            ? { ...a, ActionCompleted: true, CompletionDate: new Date().toISOString() }
            : a
        ),
      };
    });
  }

  /** Replaces the milestone customer value of a scorecard. */
  setMilestoneCustomerValue(scorecardId: number, customerValue: number): void {
    this.scorecardStore.updateById(scorecardId, (scorecard) =>
      scorecard.Milestone ? { Milestone: { ...scorecard.Milestone, CustomerValue: customerValue } } : {}
    );
  }

  /**
   * Marks a scorecard and its badge as completed. If it has linked child scorecards, the scorecards are
   * fetched again so the API can return the next scorecard in the chain unlocked.
   */
  setScorecardCompleted(scorecardId: number, completionDate: Date | string): void {
    const CompletionDate = typeof completionDate === 'string' ? completionDate : completionDate.toISOString();
    this.scorecardStore.updateById(scorecardId, { Completed: true, CompletionDate });
    this.scorecardStore.updateBadge(scorecardId, { Status: BadgeStatus.Completed, CompletionDate });

    const hasLinkedScorecards = this.scorecardStore.entities().some((scorecard) => scorecard.ParentId === scorecardId);
    if (hasLinkedScorecards) {
      this.clearCache();
      this.fetchScorecards();
    }
  }

  /** Forces the next fetch to hit the API by invalidating the store's TTL cache. */
  clearCache(): void {
    this.scorecardStore.clearCache();
  }
}
